import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NotifierType, useNotifier } from 'hooks/use-notifier';
import { useCreateRivalMutation } from 'store/api/rivals';
import { isFetchBaseQueryError } from 'store/api/utils';

import type { ArrayValues } from 'type-fest';

const BOARD_TYPE_OPTIONS = ['company', 'product', 'other'] as const;
export type BoardTypeType = ArrayValues<typeof BOARD_TYPE_OPTIONS>;

type UseBoardWizardOptions = {
  allowBlank?: boolean;
  useTemplatePicker?: boolean;
};

type CompanyType = {
  url: string;
  name: string;
};

function domainToValidURL(domain: string) {
  try {
    const url = new URL(domain);
    return url.href;
  } catch {
    try {
      const url = new URL(`https://${domain}`);
      return url.href;
    } catch {
      return null;
    }
  }
}

function isValidURL(url: string | undefined) {
  if (!url) {
    return false;
  }

  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function useBoardWizard({
  allowBlank = false,
  useTemplatePicker = false,
}: UseBoardWizardOptions) {
  const { t } = useTranslation();
  const { notify } = useNotifier();

  const [createRival, { isLoading }] = useCreateRivalMutation();

  const boardCategoryOptions = useMemo(
    () =>
      BOARD_TYPE_OPTIONS.filter((type) =>
        allowBlank ? true : type !== 'other',
      ).map((value) => ({
        value,
        label: t(`Common:boardWizard.categoryOptions.${value}`),
      })),
    [allowBlank, t],
  );
  const [boardCategory, setBoardType] = useState<BoardTypeType>();

  const [company, setCompany] = useState<CompanyType | null>();
  const [companyUrl, setCompanyUrl] = useState('');

  const [boardName, setBoardName] = useState('');

  const [copyFromId, setCopyFromId] = useState(0);

  const showCompanyPicker = ['company', 'product'].includes(
    boardCategory || '',
  );

  const showCompanyUrl = showCompanyPicker && company === null;
  const showBoardName =
    boardCategory && (boardCategory === 'other' || company || companyUrl);
  const showTemplatePicker =
    useTemplatePicker &&
    boardCategory &&
    (boardCategory === 'other' || company || companyUrl);
  const boardUrl = companyUrl || company?.url;
  const isSubmitEnabled = boardName && boardCategory && isValidURL(boardUrl);

  async function handleCreateBoard() {
    try {
      if (!boardCategory) {
        throw new Error('Board type is required');
      }

      await createRival({
        name: boardName,
        category: boardCategory,
        url: boardUrl,
        copyFromId,
        // TODO I'm probably missing some parameters here
      }).unwrap();
    } catch (error) {
      let message = t('Common:boardWizard.errors.fallback');
      if (isFetchBaseQueryError(error)) {
        // TODO Improve error handling
        // Side note: server responds 404 for name conflict, which is strange

        if (
          'data' in error &&
          error.data &&
          typeof error.data === 'object' &&
          'message' in error.data &&
          typeof error.data.message === 'string'
        ) {
          message = error.data.message;
        } else if (error instanceof Error) {
          message = error.message;
        }
      }

      notify({
        message,
        type: NotifierType.ERROR,
      });
    }
  }

  function handleBoardCategoryChange(type: BoardTypeType) {
    setBoardType((prevType) => {
      if (prevType === 'other') {
        setCompany(undefined);
      }
      if (type === 'other') {
        setBoardName('My New Blank Board');
      }
      return type;
    });
  }

  function handleSetCompany(company: CompanyType | null) {
    if (!company) {
      setCompany(null);
      return;
    }

    setCompany({
      ...company,
      url: domainToValidURL(company.url) || company.url,
    });
  }

  return {
    isLoading,
    boardCategoryOptions,
    boardCategory,
    onBoardCategoryChange: handleBoardCategoryChange,
    company,
    setCompany: handleSetCompany,
    companyUrl,
    setCompanyUrl,
    boardName,
    setBoardName,
    copyFromId,
    setCopyFromId,
    showCompanyPicker,
    showCompanyUrl,
    showBoardName,
    showTemplatePicker,
    isSubmitEnabled,
    onCreateBoard: handleCreateBoard,
  };
}
